/**
 * RainSkipPanel — skip watering for rain, per site and per valve.
 *
 * The site rule has two parts, each switched on by itself: rain that fell
 * ("at least 10 mm in the last 12 hours") and rain forecast ("at least 5 mm in
 * the next 24 hours"). Either one met skips the valve. A valve can instead
 * have its own numbers (its forecast part optional), or never be skipped: one
 * under cover gets no rain, so the rain outside is no reason to leave it dry
 * (Rob, 2026-10-12).
 *
 * The server decides each run about fifteen minutes before it starts. Fallen
 * rain is the farm's own station first, Open-Meteo where there is none or it
 * has gone quiet; the forecast is always Open-Meteo's. A skipped valve's step
 * is shortened to the valves left in it, and a step with none left is
 * dropped. Run now is never skipped. The farm's alert list is told once a day.
 */

import { useCallback, useEffect, useMemo, useState } from 'react';
import { CloudRain, Loader2, Umbrella } from 'lucide-react';
import espnowService, {
  type EspNowController,
  type EspNowRouter,
  type RainCheck,
  type RainSkipSettings,
  type ScheduleProgram,
  type SiteRainRule,
  type ValveRainMode,
  type ValveRainRule,
  relayDisplayName,
} from '../../services/espnowIrrigation';
import { errorMessage } from '../../utils/apiResponse';
import { formatDateTimeInTz } from '../../utils/timezone';
import { macKey, reasonText, ruleText, sourceText, valveKey, valveRuleText } from '../../utils/rainSkip';

interface Props {
  controller: EspNowController;
  programs: ScheduleProgram[];
  routers: EspNowRouter[];
  orgTimezone: string;
  /** Open on arrival (tests; a link that names it). */
  defaultOpen?: boolean;
}

interface Valve {
  key: string;
  router_mac: string;
  relay_index: number;
  name: string;
  programs: string[];
}

/** What a valve's own rule saves: fallen rain, and a forecast part or none. */
interface ValveNumbers {
  rain_mm: number;
  hours: number;
  forecast_mm: number | null;
  forecast_hours: number | null;
}

const DEFAULT_FORECAST_MM = 5;
const DEFAULT_FORECAST_HOURS = 24;

const inputClass = 'w-16 text-sm border border-gray-300 rounded-lg px-2 py-1.5 bg-white text-center focus:outline-none focus:ring-2 focus:ring-blue-500';

function NumberPair({
  rainMm, hours, onRain, onHours, disabled, label, ahead = false,
}: {
  rainMm: string; hours: string; onRain: (v: string) => void; onHours: (v: string) => void;
  disabled?: boolean; label: string;
  /** The forecast: "in the next" rather than "in the last". */
  ahead?: boolean;
}) {
  return (
    <span className="inline-flex flex-wrap items-center gap-1.5 text-sm text-gray-700">
      at least
      <input
        type="number" inputMode="decimal" min={0.1} max={500} step={0.5}
        value={rainMm} onChange={e => onRain(e.target.value)} disabled={disabled}
        aria-label={`${label}: rain in mm`} className={inputClass}
      />
      {ahead ? 'mm in the next' : 'mm in the last'}
      <input
        type="number" inputMode="numeric" min={1} max={72} step={1}
        value={hours} onChange={e => onHours(e.target.value)} disabled={disabled}
        aria-label={`${label}: hours`} className={inputClass}
      />
      h
    </span>
  );
}

/** A rule as typed, or the sentence saying why it is not one. */
function parseRule(rainMm: string, hours: string, what = 'Rain'): { rain_mm: number; hours: number } | string {
  const mm = Number(rainMm);
  const h = Number(hours);
  if (!Number.isFinite(mm) || mm <= 0 || mm > 500) return `${what} must be more than 0 and at most 500 mm.`;
  if (!Number.isInteger(h) || h < 1 || h > 72) return `${what} hours must be a whole number from 1 to 72.`;
  return { rain_mm: mm, hours: h };
}

function ValveRow({
  valve, site, rule, onSave,
}: {
  valve: Valve;
  site: SiteRainRule;
  rule: ValveRainRule | undefined;
  onSave: (mode: ValveRainMode, numbers?: ValveNumbers) => Promise<void>;
}) {
  const mode = rule?.mode ?? 'site';
  const savedForecast = rule?.forecast_mm != null && rule.forecast_hours != null;
  const [draftMode, setDraftMode] = useState<ValveRainMode>(mode);
  const [rainMm, setRainMm] = useState(String(rule?.rain_mm ?? site.rain_mm));
  const [hours, setHours] = useState(String(rule?.hours ?? site.hours));
  const [useForecast, setUseForecast] = useState(savedForecast);
  const [forecastMm, setForecastMm] = useState(String(rule?.forecast_mm ?? site.forecast_mm ?? DEFAULT_FORECAST_MM));
  const [forecastHours, setForecastHours] = useState(
    String(rule?.forecast_hours ?? site.forecast_hours ?? DEFAULT_FORECAST_HOURS));
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => { setDraftMode(mode); }, [mode]);

  const save = async (next: ValveRainMode) => {
    setError(null);
    let numbers: ValveNumbers | undefined;
    if (next === 'custom') {
      const parsed = parseRule(rainMm, hours);
      if (typeof parsed === 'string') { setError(parsed); return; }
      numbers = { ...parsed, forecast_mm: null, forecast_hours: null };
      if (useForecast) {
        const f = parseRule(forecastMm, forecastHours, 'Forecast');
        if (typeof f === 'string') { setError(f); return; }
        numbers = { ...numbers, forecast_mm: f.rain_mm, forecast_hours: f.hours };
      }
    }
    setSaving(true);
    try {
      await onSave(next, numbers);
    } catch (err) {
      setError(errorMessage(err, 'Could not save this valve.'));
      setDraftMode(mode);
    } finally {
      setSaving(false);
    }
  };

  const customDirty = draftMode === 'custom' && (
    mode !== 'custom'
    || String(rule?.rain_mm) !== rainMm || String(rule?.hours) !== hours
    || useForecast !== savedForecast
    || (useForecast && (String(rule?.forecast_mm) !== forecastMm || String(rule?.forecast_hours) !== forecastHours))
  );

  return (
    <li className="py-2.5 space-y-1.5">
      <div className="flex flex-wrap items-start justify-between gap-2">
        <div className="min-w-0">
          <div className="text-sm font-medium text-gray-800 break-words flex items-center gap-1.5">
            {mode === 'never' && <Umbrella className="w-3.5 h-3.5 text-gray-500 flex-shrink-0" aria-hidden />}
            {valve.name}
          </div>
          <div className="text-xs text-gray-500 break-words">
            {valveRuleText(site, rule)} · {valve.programs.join(', ')}
          </div>
        </div>
        <div className="flex items-center gap-2">
          {saving && <Loader2 className="w-3.5 h-3.5 animate-spin text-gray-500" />}
          <select
            value={draftMode}
            disabled={saving}
            aria-label={`Rain setting for ${valve.name}`}
            onChange={e => {
              const next = e.target.value as ValveRainMode;
              setDraftMode(next);
              if (next !== 'custom') void save(next);
            }}
            className="text-sm border border-gray-300 rounded-lg px-2 py-1.5 bg-white focus:outline-none focus:ring-2 focus:ring-blue-500"
          >
            <option value="site">Follow the site rule</option>
            <option value="custom">Its own rule</option>
            <option value="never">Never skip (under cover)</option>
          </select>
        </div>
      </div>
      {draftMode === 'custom' && (
        <div className="space-y-1.5 pl-0 sm:pl-5">
          <div className="flex flex-wrap items-center gap-2">
            <span className="text-sm text-gray-700">Skip if</span>
            <NumberPair
              rainMm={rainMm} hours={hours} onRain={setRainMm} onHours={setHours}
              disabled={saving} label={valve.name}
            />
            <span className="text-sm text-gray-700">fell</span>
          </div>
          <div className="flex flex-wrap items-center gap-2">
            <label className="inline-flex items-center gap-2 text-sm text-gray-700">
              <input
                type="checkbox" checked={useForecast} disabled={saving}
                onChange={e => setUseForecast(e.target.checked)}
                className="h-4 w-4 rounded border-gray-300"
              />
              or if
            </label>
            <NumberPair
              rainMm={forecastMm} hours={forecastHours} onRain={setForecastMm} onHours={setForecastHours}
              disabled={saving || !useForecast} label={`${valve.name} forecast`} ahead
            />
            <span className="text-sm text-gray-700">is forecast</span>
          </div>
          {customDirty && (
            <button
              onClick={() => void save('custom')}
              disabled={saving}
              className="px-3 py-1.5 bg-blue-600 hover:bg-blue-700 text-white text-xs font-medium rounded-lg disabled:opacity-50"
            >
              Save
            </button>
          )}
        </div>
      )}
      {error && <p className="text-xs text-red-600">{error}</p>}
    </li>
  );
}

function CheckLine({ check, orgTimezone, nameOf }: {
  check: RainCheck; orgTimezone: string; nameOf: (mac: string, relay: number) => string;
}) {
  const when = formatDateTimeInTz(check.starts_at, orgTimezone);
  const program = check.program_name || 'A program';
  if (check.skipped.length > 0) {
    const first = check.skipped[0];
    return (
      <li className="text-xs text-gray-700 break-words">
        <span className="font-medium">{when}</span> · {program}: skipped{' '}
        {check.skipped.map(s => nameOf(s.router_mac, s.relay_index)).join(', ')}.{' '}
        {reasonText(first.reason, first.rain_mm, first.hours)} ({sourceText(first.source, first.station_name)}).
      </li>
    );
  }
  const unchecked = check.rain.unchecked?.length ?? 0;
  const fell = Object.entries(check.rain.by_hours ?? {}).filter(([, mm]) => mm != null);
  const coming = Object.entries(check.rain.forecast_by_hours ?? {}).filter(([, mm]) => mm != null);
  const parts = [
    fell.length
      ? fell.map(([h, mm]) => `${mm} mm in the last ${h} h`).join(', ')
        + ` (${sourceText(check.rain.source, check.rain.station_name)})`
      : null,
    coming.length
      ? coming.map(([h, mm]) => `${mm} mm forecast in the next ${h} h`).join(', ')
      : null,
  ].filter(Boolean);
  return (
    <li className="text-xs text-gray-500 break-words">
      <span className="font-medium">{when}</span> · {program}: watered.{' '}
      {parts.length > 0 && `${parts.join('; ')}.`}
      {unchecked > 0 && ' Some rain figures could not be read, so they skipped nothing.'}
    </li>
  );
}

export default function RainSkipPanel({ controller, programs, routers, orgTimezone, defaultOpen = false }: Props) {
  const controllerId = controller.controller_id;
  const [data, setData] = useState<RainSkipSettings | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [open, setOpen] = useState(defaultOpen);

  const [enabled, setEnabled] = useState(false);
  const [rainMm, setRainMm] = useState('10');
  const [hours, setHours] = useState('12');
  const [forecastEnabled, setForecastEnabled] = useState(false);
  const [forecastMm, setForecastMm] = useState(String(DEFAULT_FORECAST_MM));
  const [forecastHours, setForecastHours] = useState(String(DEFAULT_FORECAST_HOURS));
  const [savingSite, setSavingSite] = useState(false);
  const [siteError, setSiteError] = useState<string | null>(null);

  const showSite = (s: SiteRainRule) => {
    setEnabled(s.enabled);
    setRainMm(String(s.rain_mm));
    setHours(String(s.hours));
    setForecastEnabled(Boolean(s.forecast_enabled));
    setForecastMm(String(s.forecast_mm ?? DEFAULT_FORECAST_MM));
    setForecastHours(String(s.forecast_hours ?? DEFAULT_FORECAST_HOURS));
  };

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const d = await espnowService.getRainSkip(controllerId);
      setData(d);
      showSite(d.site);
    } catch (err) {
      setError(errorMessage(err, 'Could not load the rain settings.'));
    } finally {
      setLoading(false);
    }
  }, [controllerId]);

  useEffect(() => { void load(); }, [load]);

  const nameOf = useCallback((mac: string, relay: number) => {
    const router = routers.find(r => macKey(r.router_mac) === macKey(mac));
    const label = relayDisplayName(null, router?.relay_labels, relay, '');
    return label || `${router?.name || mac.slice(-8)} / Relay ${relay}`;
  }, [routers]);

  // Every valve a program on this site opens, once each.
  const valves = useMemo<Valve[]>(() => {
    const byKey = new Map<string, Valve>();
    for (const p of programs) {
      for (const step of p.steps || []) {
        for (const r of step.relays || []) {
          if (!r.router_mac) continue;
          const key = valveKey(r.router_mac, r.relay_index);
          const v = byKey.get(key) ?? {
            key, router_mac: r.router_mac, relay_index: r.relay_index,
            name: nameOf(r.router_mac, r.relay_index), programs: [],
          };
          if (!v.programs.includes(p.name)) v.programs.push(p.name);
          byKey.set(key, v);
        }
      }
    }
    return [...byKey.values()].sort((a, b) => a.name.localeCompare(b.name));
  }, [programs, nameOf]);

  const rulesByKey = useMemo(
    () => new Map((data?.valves ?? []).map(r => [valveKey(r.router_mac, r.relay_index), r])),
    [data],
  );

  const site: SiteRainRule = data?.site ?? { enabled: false, rain_mm: 10, hours: 12 };
  const siteDirty = data != null && (
    enabled !== data.site.enabled || rainMm !== String(data.site.rain_mm) || hours !== String(data.site.hours)
    || forecastEnabled !== Boolean(data.site.forecast_enabled)
    || forecastMm !== String(data.site.forecast_mm ?? DEFAULT_FORECAST_MM)
    || forecastHours !== String(data.site.forecast_hours ?? DEFAULT_FORECAST_HOURS)
  );

  const saveSite = async () => {
    const parsed = parseRule(rainMm, hours);
    if (typeof parsed === 'string') { setSiteError(parsed); return; }
    const forecast = parseRule(forecastMm, forecastHours, 'Forecast');
    if (typeof forecast === 'string') { setSiteError(forecast); return; }
    setSavingSite(true);
    setSiteError(null);
    try {
      const saved = await espnowService.updateRainSkip(controllerId, {
        enabled, ...parsed,
        forecast_enabled: forecastEnabled, forecast_mm: forecast.rain_mm, forecast_hours: forecast.hours,
      });
      setData(d => (d ? { ...d, site: saved } : d));
      showSite(saved);
    } catch (err) {
      setSiteError(errorMessage(err, 'Could not save the rain rule.'));
    } finally {
      setSavingSite(false);
    }
  };

  const saveValve = async (valve: Valve, mode: ValveRainMode, numbers?: ValveNumbers) => {
    const saved = await espnowService.setValveRainRule(controllerId, {
      router_mac: valve.router_mac, relay_index: valve.relay_index, mode, ...(numbers ?? {}),
    });
    setData(d => {
      if (!d) return d;
      const others = d.valves.filter(r => valveKey(r.router_mac, r.relay_index) !== valve.key);
      return { ...d, valves: mode === 'site' ? others : [...others, saved] };
    });
  };

  const ownRules = (data?.valves ?? []).filter(r => r.mode === 'custom').length;
  const covered = (data?.valves ?? []).filter(r => r.mode === 'never').length;
  const siteOn = site.enabled || Boolean(site.forecast_enabled);
  const siteParts = [
    site.enabled ? `${ruleText(site.rain_mm, site.hours)} fell` : null,
    site.forecast_enabled && site.forecast_mm != null && site.forecast_hours != null
      ? `${site.forecast_mm} mm forecast in ${site.forecast_hours} h` : null,
  ].filter(Boolean);
  const summary = loading
    ? 'Loading…'
    : !siteOn && ownRules === 0
      ? 'Off: every valve waters whatever the rain'
      : [
          siteOn ? `On: skip if ${siteParts.join(' or ')}` : 'Site rule off',
          ownRules ? `${ownRules} valve${ownRules === 1 ? '' : 's'} with its own rule` : null,
          covered ? `${covered} never skipped` : null,
        ].filter(Boolean).join(' · ');

  const recent = (data?.recent ?? []).slice(0, 8);

  return (
    <section className="bg-white border border-gray-200 rounded-xl mb-4" aria-label="Rain skip">
      <button
        onClick={() => setOpen(o => !o)}
        aria-expanded={open}
        className="w-full flex items-start gap-3 p-4 text-left"
      >
        <CloudRain className={`w-5 h-5 mt-0.5 flex-shrink-0 ${siteOn || ownRules ? 'text-blue-600' : 'text-gray-500'}`} />
        <div className="min-w-0 flex-1">
          <div className="text-sm font-semibold text-gray-900">Skip watering for rain</div>
          <div className="text-xs text-gray-500 break-words">{summary}</div>
        </div>
        <span className="text-xs text-blue-600 flex-shrink-0">{open ? 'Hide' : 'Set up'}</span>
      </button>

      {open && (
        <div className="px-4 pb-4 space-y-4 border-t border-gray-100 pt-4">
          {error && <div className="p-3 bg-red-50 border border-red-200 rounded-lg text-sm text-red-700">{error}</div>}

          {data && (
            <>
              <p className="text-xs text-gray-600">
                Each run is checked about 15 minutes before it starts: the rain in the hours before then, and the
                rain forecast for the hours after. Either one met skips the valve. A skipped valve's step runs only
                as long as the valves left in it, and a pump serving only skipped valves stays off. Run now always
                waters. The farm's alert list is told once a day.
              </p>

              {!data.has_farm ? (
                <p className="text-xs p-2.5 rounded-lg bg-amber-50 border border-amber-200 text-amber-800">
                  This site is not linked to a farm, so there is nowhere to read rain from and nothing will be
                  skipped. Link it to a farm on the Settings tab.
                </p>
              ) : (
                <p className="text-xs text-gray-500">
                  {data.has_station
                    ? 'Rain that fell comes from the farm\'s weather station. If it stops reporting, Open-Meteo\'s modelled rainfall is used instead, and the record says so.'
                    : 'This farm has no weather station, so rain that fell comes from Open-Meteo\'s modelled rainfall for the farm\'s location.'}
                  {' '}The forecast is always Open-Meteo's.
                </p>
              )}

              {data.transport === 'xbee' && (
                <p className="text-xs text-gray-500">
                  The bridge leaves skipped valves out of the run. A bridge older than 13 Oct 2026 waters them
                  anyway, and the live map says so.
                </p>
              )}

              {/* Site rule */}
              <div className="space-y-2">
                <h4 className="text-xs font-medium text-gray-500 uppercase tracking-wide">Site rule</h4>
                <div className="flex flex-wrap items-center gap-2">
                  <label className="inline-flex items-center gap-2 text-sm font-medium text-gray-800">
                    <input
                      type="checkbox"
                      checked={enabled}
                      onChange={e => setEnabled(e.target.checked)}
                      className="h-4 w-4 rounded border-gray-300"
                    />
                    Skip if
                  </label>
                  <NumberPair
                    rainMm={rainMm} hours={hours} onRain={setRainMm} onHours={setHours}
                    disabled={savingSite || !enabled} label="Site rule"
                  />
                  <span className="text-sm text-gray-700">fell</span>
                </div>
                <div className="flex flex-wrap items-center gap-2">
                  <label className="inline-flex items-center gap-2 text-sm font-medium text-gray-800">
                    <input
                      type="checkbox"
                      checked={forecastEnabled}
                      onChange={e => setForecastEnabled(e.target.checked)}
                      className="h-4 w-4 rounded border-gray-300"
                    />
                    Skip if
                  </label>
                  <NumberPair
                    rainMm={forecastMm} hours={forecastHours} onRain={setForecastMm} onHours={setForecastHours}
                    disabled={savingSite || !forecastEnabled} label="Site forecast" ahead
                  />
                  <span className="text-sm text-gray-700">is forecast</span>
                </div>
                {siteDirty && (
                  <button
                    onClick={() => void saveSite()}
                    disabled={savingSite}
                    className="px-3 py-1.5 bg-blue-600 hover:bg-blue-700 text-white text-xs font-medium rounded-lg disabled:opacity-50 inline-flex items-center gap-1"
                  >
                    {savingSite && <Loader2 className="w-3 h-3 animate-spin" />} Save
                  </button>
                )}
                {siteError && <p className="text-xs text-red-600">{siteError}</p>}
              </div>

              {/* Per valve */}
              <div>
                <h4 className="text-xs font-medium text-gray-500 uppercase tracking-wide">Each valve</h4>
                {valves.length === 0 ? (
                  <p className="text-xs text-gray-500 mt-1">No program on this site opens a valve yet.</p>
                ) : (
                  <ul className="divide-y divide-gray-100">
                    {valves.map(v => (
                      <ValveRow
                        key={v.key}
                        valve={v}
                        site={site}
                        rule={rulesByKey.get(v.key)}
                        onSave={(mode, numbers) => saveValve(v, mode, numbers)}
                      />
                    ))}
                  </ul>
                )}
              </div>

              {/* What it has done */}
              {recent.length > 0 && (
                <div>
                  <h4 className="text-xs font-medium text-gray-500 uppercase tracking-wide">Last three days</h4>
                  <ul className="mt-1 space-y-1">
                    {recent.map((c, i) => (
                      <CheckLine key={`${c.program_id}-${c.starts_at}-${i}`} check={c}
                        orgTimezone={orgTimezone} nameOf={nameOf} />
                    ))}
                  </ul>
                </div>
              )}
            </>
          )}
        </div>
      )}
    </section>
  );
}
